import { Loader2 } from "lucide-react";
import { useCallback } from "react";

import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import { AnalyticsProvider } from "../Analytics/AnalyticsProvider";
import { LogOutTracker } from "../Analytics/LogOutTracker";
import { UserIdentifier } from "../Analytics/UserIdentifier";
import { CallManager } from "../Call/CallManager";
import { NetworkStatusProvider } from "../NetworkStatus/NetworkStatusProvider";
import { PlatformProvider } from "../Platform/PlatformProvider";
import { DesktopAuth } from "./DesktopAuth";
import { useAuth } from "./UnifiedAuthProvider";

const POSTHOG_ENABLED = import.meta.env.VITE_POSTHOG_ENABLED;
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;

export function DesktopApp() {
  const { isAuthenticated, isLoading, authenticate, clearCoreAuthState } =
    useAuth();

  const handleDesktopLogout = useCallback(() => {
    clearCoreAuthState();
    window.location.reload();
  }, [clearCoreAuthState]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <AnalyticsProvider
        posthogKey={POSTHOG_KEY}
        posthogEnabled={POSTHOG_ENABLED}
      >
        <LogOutTracker />
        <DesktopAuth onAuthenticate={(code) => authenticate({ code })} />
      </AnalyticsProvider>
    );
  }

  return (
    <PlatformProvider logout={handleDesktopLogout}>
      <AnalyticsProvider
        posthogKey={POSTHOG_KEY}
        posthogEnabled={POSTHOG_ENABLED}
      >
        <NetworkStatusProvider>
          <TooltipProvider>
            <main className="h-screen" data-testid="app-loaded">
              <UserIdentifier />
              <CallManager />
            </main>
          </TooltipProvider>
          <Toaster />
        </NetworkStatusProvider>
      </AnalyticsProvider>
    </PlatformProvider>
  );
}
